import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Paper,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useTheme,
    Divider,
    Switch,
    FormControlLabel,
    Dialog,
    IconButton
} from '@mui/material';
import {
    Check as CheckIcon,
    Star as StarIcon,
    Speed as SpeedIcon,
    Security as SecurityIcon,
    Assessment as AssessmentIcon,
    BusinessCenter as BusinessIcon,
    Api as ApiIcon,
    Support as SupportIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Email as EmailIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import StripeWrapper from '../../components/Purchase/StripeWrapper';
import scanApi from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ContactSalesModal from '../../components/Contact/ContactSalesModal';

const plans = [
    {
        id: 'basic',
        name: 'Basic',
        price: 299,
        scans: 1,
        scanPrice: 299,
        duration: 'No expiration',
        recommended: false,
        features: [
            'Full Website Security Scan',
            'Basic Vulnerability Report',
            'Email Support',
            'GDPR Compliance Check',
            'SSL Security Analysis'
        ],
        color: '#4CAF50'
    },
    {
        id: 'starter',
        name: 'Starter Pack',
        price: 899,
        scans: 5,
        scanPrice: 179,
        duration: 'No expiration',
        recommended: true,
        features: [
            '5 Full Security Scans',
            'Advanced Vulnerability Reports',
            'Priority Email Support',
            'GDPR & PCI DSS Compliance',
            'Custom Scan Scheduling',
            'Incident Response Support',
            'SSL/TLS Analysis'
        ],
        color: '#2196F3'
    },
    {
        id: 'pro',
        name: 'Professional',
        price: 1990,
        scans: 10,
        scanPrice: 199,
        duration: 'No expiration',
        recommended: false,
        features: [
            '10 Enterprise-Grade Scans',
            'Premium Vulnerability Reports',
            '24/7 Priority Support',
            'Full Compliance Suite',
            'Custom Scan Profiles',
            'Team Collaboration',
            'Advanced Analytics Dashboard',
            'Dedicated Account Manager'
        ],
        color: '#9C27B0'
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        price: 3990,
        scans: 20,
        scanPrice: 199,
        duration: 'No expiration',
        recommended: false,
        features: [
            '20 Enterprise Scans',
            'White-Label Reports',
            '24/7 Premium Support',
            'Unlimited Everything',
            'Custom Integration',
            'On-Premise Option',
            'MSP/MSSP Features',
            'Custom SLA',
            'Training & Workshops',
            'Unlimited API Access'
        ],
        color: '#FF9800'
    }
];

// Add FAQ data
const faqItems = [
    {
        question: "How do scan credits work?",
        answer: "Each scan credit allows you to perform one complete security assessment of a website. Credits are valid for the duration specified in your plan and can be used anytime during that period."
    },
    {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards (Visa, MasterCard, American Express) via our secure Stripe payment gateway. For Enterprise plans, we also accept bank transfers."
    },
    {
        question: "Can I upgrade my plan later?",
        answer: "Yes, you can upgrade your plan at any time. The remaining value of your current plan will be prorated and applied to your new plan."
    },
    {
        question: "What happens if I don't use all my scans?",
        answer: "Scan credits are valid for the duration specified in your plan. While they don't roll over after expiration, we send reminders to help you make the most of your credits."
    },
    {
        question: "Do you offer refunds?",
        answer: "We offer a full refund within 14 days of purchase if you're not satisfied with our service, provided you haven't used more than one scan credit."
    },
    {
        question: "What's included in a security scan?",
        answer: "Our scans include comprehensive vulnerability assessments, covering OWASP Top 10, custom security rules, compliance checks (GDPR, PCI DSS), and detailed reporting with remediation guidance."
    }
];

const PlansPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [clientSecret, setClientSecret] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [checkoutError, setCheckoutError] = useState(null);

    const handlePlanSelect = async (plan) => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: '/plans' } });
            return;
        }

        try {
            const response = await scanApi.createPaymentIntent({
                planId: plan.id,
                amount: plan.price,
                metadata: {
                    planName: plan.name,
                    scanCredits: plan.scans
                }
            });
            
            if (response.clientSecret) {
                setClientSecret(response.clientSecret);
                setSelectedPlan(plan);
            }
        } catch (error) {
            console.error('Payment initialization failed:', error);
            setCheckoutError(error.message);
        }
    };

    // Add success and error handlers
    const handlePaymentSuccess = async () => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const paymentIntentId = urlParams.get('payment_intent');
            
            if (paymentIntentId) {
                await scanApi.confirmPayment(paymentIntentId);
                await scanApi.refreshQuota();
                navigate('/payment/success');
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            setCheckoutError('Failed to process payment');
        }
    };

    const handlePaymentError = (error) => {
        setCheckoutError(error.message);
    };

    return (
        <Container maxWidth="lg" sx={{ py: 8 }}>
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 8 }}>
                <Typography
                    variant="h2"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Security at Every Level
                </Typography>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                    Choose the perfect plan for your security needs
                </Typography>
            </Box>

            {/* Plans Grid */}
            <Grid container spacing={4} justifyContent="center">
                {plans.map((plan) => (
                    <Grid item xs={12} md={6} lg={3} key={plan.id}>
                        <Paper
                            elevation={plan.recommended ? 12 : 4}
                            sx={{
                                p: 4,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-8px)'
                                },
                                border: plan.recommended ? `2px solid ${plan.color}` : 'none'
                            }}
                        >
                            {plan.recommended && (
                                <Chip
                                    label="Most Popular"
                                    color="primary"
                                    sx={{
                                        position: 'absolute',
                                        top: -12,
                                        right: 24,
                                        bgcolor: plan.color
                                    }}
                                />
                            )}

                            <Typography variant="h4" gutterBottom sx={{ color: plan.color }}>
                                {plan.name}
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h3" component="div" gutterBottom>
                                    ${plan.price}
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    {plan.duration}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    ${plan.scanPrice} per scan
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <List dense sx={{ flexGrow: 1 }}>
                                {plan.features.map((feature, index) => (
                                    <ListItem key={index} disableGutters>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <CheckIcon sx={{ color: plan.color }} />
                                        </ListItemIcon>
                                        <ListItemText primary={feature} />
                                    </ListItem>
                                ))}
                            </List>

                            <Button
                                variant={plan.recommended ? "contained" : "outlined"}
                                size="large"
                                fullWidth
                                sx={{
                                    mt: 3,
                                    bgcolor: plan.recommended ? plan.color : 'transparent',
                                    borderColor: plan.color,
                                    '&:hover': {
                                        bgcolor: plan.recommended 
                                            ? darken(plan.color, 0.1)
                                            : alpha(plan.color, 0.1)
                                    }
                                }}
                                onClick={() => handlePlanSelect(plan)}
                            >
                                Choose {plan.name}
                            </Button>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Features Section */}
            <Box sx={{ mt: 12 }}>
                <Typography variant="h3" gutterBottom textAlign="center">
                    Why Choose ExploitFinder?
                </Typography>
                
                <Grid container spacing={4} sx={{ mt: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h5" gutterBottom>
                                Enterprise-Grade Security
                            </Typography>
                            <Typography color="text.secondary">
                                Advanced vulnerability scanning with comprehensive security assessments
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <AssessmentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h5" gutterBottom>
                                Compliance Ready
                            </Typography>
                            <Typography color="text.secondary">
                                Built-in compliance checks for GDPR, PCI DSS, and ISO 27001
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <ApiIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                            <Typography variant="h5" gutterBottom>
                                API Integration
                            </Typography>
                            <Typography color="text.secondary">
                                Seamless integration with your existing security tools and workflows
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Enhanced FAQ Section */}
            <Box sx={{ mt: 12 }}>
                <Typography 
                    variant="h3" 
                    gutterBottom 
                    textAlign="center"
                    sx={{ mb: 6 }}
                >
                    Frequently Asked Questions
                </Typography>
                
                <Grid container spacing={4}>
                    {faqItems.map((item, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    backgroundColor: 'background.paper',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 2
                                    }
                                }}
                            >
                                <Typography 
                                    variant="h6" 
                                    gutterBottom
                                    sx={{ 
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1
                                    }}
                                >
                                    <QuestionAnswerIcon 
                                        sx={{ 
                                            fontSize: 20,
                                            mt: 0.5,
                                            color: 'primary.light'
                                        }} 
                                    />
                                    {item.question}
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ pl: 3.5 }}
                                >
                                    {item.answer}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Contact Section */}
                <Box 
                    sx={{ 
                        mt: 8, 
                        textAlign: 'center',
                        p: 4,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography variant="h5" gutterBottom>
                        Need More Information?
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Our security experts are here to help you choose the right plan
                    </Typography>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<EmailIcon />}
                        onClick={() => window.location.href = 'mailto:sales@exploitfinder.com'}
                    >
                        Contact Enterprise Sales
                    </Button>
                </Box>
            </Box>

            <Dialog
                open={Boolean(clientSecret)}
                onClose={() => {
                    setClientSecret(null);
                    setSelectedPlan(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <Box sx={{ position: 'relative', p: 3 }}>
                    <IconButton
                        onClick={() => {
                            setClientSecret(null);
                            setSelectedPlan(null);
                        }}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {clientSecret && (
                        <StripeWrapper
                            clientSecret={clientSecret}
                            plan={selectedPlan}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                        />
                    )}

                    {checkoutError && (
                        <Typography 
                            color="error" 
                            variant="body2" 
                            sx={{ mt: 2 }}
                        >
                            {checkoutError}
                        </Typography>
                    )}
                </Box>
            </Dialog>
        </Container>
    );
};

export default PlansPage;
